/**
 * Scenario Analysis View
 * 
 * Create and analyze what-if scenarios.
 */

import React, { useState } from 'react';
import { Target, Play, Loader2, Plus, Trash2 } from 'lucide-react';

export default function ScenarioAnalysis({ apiUrl }) {
  const [scenario, setScenario] = useState({
    name: '',
    type: 'conflict',
    target: '',
    aggressor: '',
    actors: [],
    severity: 'medium'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [newActor, setNewActor] = useState('');

  const scenarioTypes = ['conflict', 'crisis', 'economic', 'diplomatic', 'hybrid'];
  const severityLevels = ['low', 'medium', 'high', 'critical'];

  async function runAnalysis() {
    if (!scenario.name) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/scenario/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario })
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function runSimulation() {
    if (!scenario.name) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/scenario/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario })
      });
      const data = await response.json();
      setResult(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function addActor() {
    if (newActor && !scenario.actors.includes(newActor)) {
      setScenario({ ...scenario, actors: [...scenario.actors, newActor] });
      setNewActor('');
    }
  }

  function removeActor(actor) {
    setScenario({ ...scenario, actors: scenario.actors.filter(a => a !== actor) });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Scenario Analysis</h1>
        <p className="text-slate-400">Model and simulate geopolitical scenarios</p>
      </div>

      {/* Scenario Builder */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-lg font-semibold mb-4">Build Scenario</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Scenario Name</label>
            <input
              type="text"
              value={scenario.name}
              onChange={(e) => setScenario({ ...scenario, name: e.target.value })}
              placeholder="e.g., Taiwan Contingency"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Scenario Type</label>
            <select
              value={scenario.type}
              onChange={(e) => setScenario({ ...scenario, type: e.target.value })}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
            >
              {scenarioTypes.map(t => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Target</label>
            <input
              type="text"
              value={scenario.target}
              onChange={(e) => setScenario({ ...scenario, target: e.target.value })}
              placeholder="e.g., Taiwan"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Aggressor</label>
            <input
              type="text"
              value={scenario.aggressor}
              onChange={(e) => setScenario({ ...scenario, aggressor: e.target.value })}
              placeholder="e.g., China"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Severity</label>
            <select
              value={scenario.severity}
              onChange={(e) => setScenario({ ...scenario, severity: e.target.value })}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
            >
              {severityLevels.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Involved Actors</label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={newActor}
                onChange={(e) => setNewActor(e.target.value)}
                placeholder="Add actor..."
                className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
                onKeyDown={(e) => e.key === 'Enter' && addActor()}
              />
              <button
                onClick={addActor}
                className="px-3 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            {scenario.actors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {scenario.actors.map((actor, i) => (
                  <span key={i} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded flex items-center">
                    {actor}
                    <button onClick={() => removeActor(actor)} className="ml-2 hover:text-red-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex space-x-4">
          <button
            onClick={runAnalysis}
            disabled={loading || !scenario.name}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-500 rounded-lg font-medium
                     disabled:opacity-50 flex items-center"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Target className="w-5 h-5 mr-2" />}
            Analyze
          </button>
          <button
            onClick={runSimulation}
            disabled={loading || !scenario.name}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-lg font-medium
                     disabled:opacity-50 flex items-center"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Play className="w-5 h-5 mr-2" />}
            Simulate
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Outcomes */}
          {result.outcomes && (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Outcomes</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-sm text-slate-400">Favorability</div>
                  <div className="text-2xl font-bold text-cyan-400">
                    {((result.outcomes.favorability || 0) * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-sm text-slate-400">Escalation Risk</div>
                  <div className="text-2xl font-bold text-yellow-400">
                    {((result.outcomes.escalationPotential || 0) * 100).toFixed(0)}%
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-sm text-slate-400">Risks Identified</div>
                  <div className="text-2xl font-bold text-red-400">
                    {result.outcomes.risks?.length || 0}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {result.recommendations?.length > 0 && (
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
              <div className="space-y-2">
                {result.recommendations.map((rec, i) => (
                  <div key={i} className="p-3 bg-slate-700/50 rounded-lg">
                    <span className="font-medium">{rec.action || rec.type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Data */}
          <details className="bg-slate-800 rounded-xl border border-slate-700">
            <summary className="p-4 cursor-pointer hover:bg-slate-700/50">
              View Raw Result Data
            </summary>
            <pre className="p-4 text-xs overflow-auto max-h-96 bg-slate-900">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
